import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../config';
import SkillManagement from '../components/SkillManagement';
import './Settings.scss';

/**
 * UserProfile stellt das persönliche Dashboard für den angemeldeten Benutzer bereit.
 * Ermöglicht die Verwaltung von Profildaten (Name, Bio), Wohnadresse (für Rechnungen),
 * Passwörtern und bei Dozenten die Verwaltung ihrer Qualifikationen (Skills).
 */
export default function UserProfile() {
  const { user, token, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const [plz, setPlz] = useState('');
  const [ort, setOrt] = useState('');
  const [strasseHausnummer, setStrasseHausnummer] = useState('');
  const [originalAddress, setOriginalAddress] = useState(null);
  const [addressLoading, setAddressLoading] = useState(false);

  /**
   * Lädt die bestehende Adresse des Benutzers aus dem Backend.
   */
  useEffect(() => {
    const fetchAddress = async () => {
      if (!token) return;
      setAddressLoading(true);
      try {
        const response = await fetch(`${getBackendUrl()}/user/me/adresse`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPlz(data.plz || '');
          setOrt(data.ort || '');
          setStrasseHausnummer(data.strasseHausnummer || '');
          setOriginalAddress(data);
        }
      } catch (err) {
        console.warn("Adresse konnte nicht geladen werden:", err);
      } finally {
        setAddressLoading(false);
      }
    };
    fetchAddress();
  }, [token]);

  useEffect(() => {
    if (!isEditing) {
      setName(user?.name || '');
      setBio(user?.bio || '');
    }
  }, [user, isEditing]);

  /**
   * Aktualisiert das allgemeine Profil (Name und Bio).
   */
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await updateProfile(name, bio);
      setSuccess('Profil erfolgreich aktualisiert.');
      setIsEditing(false);
    } catch (err) {
      setError(err.message || 'Fehler beim Aktualisieren des Profils.');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePasswordChange = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (password !== confirmPassword) {
      return setError('Die Passwörter stimmen nicht überein.');
    }
    setSuccess('Die Funktion zum Ändern des Passworts ist noch nicht verfügbar.');
  };

  /**
   * Aktualisiert die Adresse im Backend.
   */
  const handleAddressUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!strasseHausnummer || !plz || !ort) {
      setError('Bitte füllen Sie alle Adressfelder aus.');
      return;
    }

    const parsedPlz = parseInt(plz, 10);
    if (isNaN(parsedPlz)) {
      setError('Postleitzahl muss eine Zahl sein.');
      return;
    }

    if (
      originalAddress &&
      originalAddress.plz === parsedPlz &&
      originalAddress.ort === ort &&
      originalAddress.strasseHausnummer === strasseHausnummer
    ) {
      setSuccess('Adresse ist bereits aktuell (keine Änderung vorgenommen).');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${getBackendUrl()}/user/me/adresse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ plz: parsedPlz, ort, strasseHausnummer })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: 'Speichern fehlgeschlagen.' }));
        throw new Error(errData.message || 'Speichern der Adresse fehlgeschlagen.');
      }

      setOriginalAddress({ plz: parsedPlz, ort, strasseHausnummer });
      setSuccess('Adresse erfolgreich gespeichert.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vintage-settings p-4" style={{ backgroundColor: '#fcfaf8', minHeight: '100vh', borderRadius: '12px' }}>
      <h1 className="page-header" style={{ color: '#2c3e50', fontWeight: '700' }}>Dein Profil</h1>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card className="settings-card mb-4 shadow-sm border-0">
        <Card.Header className="d-flex justify-content-between align-items-center bg-white border-bottom">
          <span style={{ fontWeight: '600' }}>Profil bearbeiten</span>
          <Button variant={isEditing ? 'outline-secondary' : 'outline-primary'} size="sm" onClick={() => setIsEditing(!isEditing)}>
            {isEditing ? 'Abbrechen' : 'Bearbeiten'}
          </Button>
        </Card.Header>
        <Form onSubmit={handleProfileUpdate}>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Vollständiger Name</Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!isEditing}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Beschreibung / Bio</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Erzähle etwas über dich..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                disabled={!isEditing}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>E-Mail-Adresse</Form.Label>
              <Form.Control
                type="email"
                value={user?.email || ''}
                disabled
              />
            </Form.Group>
          </Card.Body>
          {isEditing && (
            <Card.Footer className="bg-white border-top">
              <Button type="submit" className="form-button" disabled={loading} style={{ backgroundColor: '#b95c50', borderColor: '#b95c50' }}>
                {loading ? <Spinner size="sm" /> : 'Profil speichern'}
              </Button>
            </Card.Footer>
          )}
        </Form>
      </Card>

      <Card className="settings-card mb-4 shadow-sm border-0">
        <Card.Header className="bg-white border-bottom"><span style={{ fontWeight: '600' }}>Adresse verwalten</span></Card.Header>
        <Form onSubmit={handleAddressUpdate}>
          <Card.Body>
            {addressLoading ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Adresse wird geladen...</span>
              </div>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Straße & Hausnummer</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Musterstraße 123"
                    value={strasseHausnummer}
                    onChange={(e) => setStrasseHausnummer(e.target.value)}
                    required
                  />
                </Form.Group>
                <div className="row">
                  <div className="col-md-4">
                    <Form.Group className="mb-3">
                      <Form.Label>PLZ</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="12345"
                        value={plz}
                        onChange={(e) => setPlz(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-8">
                    <Form.Group className="mb-3">
                      <Form.Label>Ort</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Musterstadt"
                        value={ort}
                        onChange={(e) => setOrt(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </div>
                </div>
              </>
            )}
          </Card.Body>
          {!addressLoading && (
            <Card.Footer className="bg-white border-top">
              <Button type="submit" className="form-button" disabled={loading} style={{ backgroundColor: '#b95c50', borderColor: '#b95c50' }}>
                {loading ? <Spinner size="sm" /> : 'Adresse speichern'}
              </Button>
            </Card.Footer>
          )}
        </Form>
      </Card>

      {user?.role === 'dozent' && (
        <div className="mb-4">
          <SkillManagement />
        </div>
      )}

      <Card className="settings-card shadow-sm border-0">
        <Card.Header className="bg-white border-bottom"><span style={{ fontWeight: '600' }}>Passwort ändern</span></Card.Header>
        <Form onSubmit={handlePasswordChange}>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Neues Passwort</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Neues Passwort bestätigen</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Form.Group>
          </Card.Body>
          <Card.Footer className="bg-white border-top">
            <Button type="submit" className="form-button" disabled={loading} style={{ backgroundColor: '#b95c50', borderColor: '#b95c50' }}>
              {loading ? <Spinner size="sm" /> : 'Passwort aktualisieren'}
            </Button>
          </Card.Footer>
        </Form>
      </Card>
    </div>
  );
}
